import ErrorCircleSvg from "@/src/assets/icons/icon-error-circle.svg";
import EyeOffSvg from "@/src/assets/icons/icon-eye-off.svg";
import EyeSvg from "@/src/assets/icons/icon-eye.svg";
import { Button } from "@/src/components/atom/Button";
import Icon from "@/src/components/atom/Icon";
import IconInput from "@/src/components/atom/Input/IconInput";
import Typo from "@/src/components/atom/Typo";
import SignUpLayout from "@/src/components/template/Layout/SignUpLayout";
import { SUBSCRIPTION_EMPTY_LIST_ERROR_CODE } from "@/src/constant/error";
import useAlert from "@/src/hooks/useAlert";
import useRedirectNavigate from "@/src/hooks/useRedirectNavigate";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import IMPORTER_PRIVATE_PATH from "@/src/routes/importer/path";
import PUBLIC_PATH from "@/src/routes/public/path";
import { useAppDispatch, useAppSelector } from "@/src/store";
import { useChangePasswordMutation } from "@/src/store/apis/auth";
import {
  useLazyGetCurrentSubscriptionQuery,
  useLazyGetEnterpriseInquiriesQuery,
  useLazyGetPlansQuery,
} from "@/src/store/apis/subscription";
import {
  setTempUser,
  setUser,
  setUserTemporaryPasswordCheck,
} from "@/src/store/slice/auth";
import colorSet from "@/src/styles/color";
import typo from "@/src/styles/typography";
import { passwordRegex } from "@/src/utils/regex";
import { utcNow } from "@/src/utils/utcDayjs";
import dayjs from "dayjs";
import { useCallback, useEffect, useReducer, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

type PasswordType = "text" | "password";

type State = {
  currentPassword: PasswordType;
  newPassword: PasswordType;
  reNewPassword: PasswordType;
};

type Action =
  | { type: "currentPassword" }
  | { type: "newPassword" }
  | { type: "reNewPassword" };

const initialState: State = {
  currentPassword: "password",
  newPassword: "password",
  reNewPassword: "password",
};

export const changePasswordReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "currentPassword":
      return {
        ...state,
        currentPassword:
          state.currentPassword === "password" ? "text" : "password",
      };
    case "newPassword":
      return {
        ...state,
        newPassword: state.newPassword === "password" ? "text" : "password",
      };
    case "reNewPassword":
      return {
        ...state,
        reNewPassword: state.reNewPassword === "password" ? "text" : "password",
      };
  }
};

const ChangePasswordPage = () => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      rePassword: "",
    },
  });
  const [passwordVisibleState, passwordTypeDispatch] = useReducer(
    changePasswordReducer,
    initialState
  );
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const tempUser = useAppSelector((state) => state.auth.tempUser);
  const alert = useAlert();
  const redirectOrNavigate = useRedirectNavigate();

  // API
  const [changePassword] = useChangePasswordMutation();
  const [getCurrentSubscription] = useLazyGetCurrentSubscriptionQuery();
  const [getPlans] = useLazyGetPlansQuery();
  const [getEnterpriseInquiries] = useLazyGetEnterpriseInquiriesQuery();
  const ref = useRef<HTMLInputElement>(null);

  const companyType =
    user?.exporterUserType === "CORPORATE_MANAGER"
      ? user?.exporter.companyType
      : user?.exporterUserMainFieldType;
  const isBuyerType = companyType === "BUYER";

  const goTo = useCallback(
    (path: keyof typeof PUBLIC_PATH) => {
      redirectOrNavigate(PUBLIC_PATH[path], { replace: true });
    },
    [redirectOrNavigate]
  );

  const redirectUserByType = async () => {
    if (user === null) {
      return;
    }

    try {
      const subscription = await getCurrentSubscription().unwrap();
      await getPlans({ planType: "ENTERPRISE" });
      await getEnterpriseInquiries({
        page: 1,
        pageSize: 1,
      });

      dispatch(setTempUser(null));

      switch (companyType) {
        case "BUYER":
          return redirectOrNavigate(IMPORTER_PRIVATE_PATH.HOME);
        case "SELLER":
          return redirectOrNavigate(EXPORTER_PRIVATE_PATH.HOME);
        case "BOTH":
          if (subscription.subscriptionStatus === "CANCELED") {
            return redirectOrNavigate(
              EXPORTER_PRIVATE_PATH.SUBSCRIPTION_LANDING
            );
          }
          return redirectOrNavigate(EXPORTER_PRIVATE_PATH.HOME);
        default:
          return redirectOrNavigate(EXPORTER_PRIVATE_PATH.HOME);
      }
    } catch (e: any) {
      // 구독 내역이 없는 경우, 수출자, BOTH
      if (e.data.errorCode === SUBSCRIPTION_EMPTY_LIST_ERROR_CODE) {
        return redirectOrNavigate(EXPORTER_PRIVATE_PATH.SUBSCRIPTION_LANDING);
      }

      alert.showAlert({
        message: Array.isArray(e.data.message)
          ? e.data.message[0]
          : e.data.message,
        type: "error",
      });
    }
  };

  const handleChangePassword = async () => {
    if (!user) {
      alert.showAlert({
        message: t("alert:somethingWrong"),
        type: "error",
      });
      return goTo("LOGIN");
    }

    try {
      await changePassword({
        aId: user.aId,
        password: getValues("newPassword"),
        oldPassword: getValues("currentPassword"),
      }).unwrap();

      dispatch(setUserTemporaryPasswordCheck(false));
      redirectUserByType();

      alert.showAlert({
        message: t("alert:changeYourPassword"),
      });
    } catch (e: any) {
      if (e.status === 400) {
        return alert.showAlert({
          message: t("alert:yourConfirmPasswordDoesNotMatch"),
          type: "error",
        });
      }

      alert.showAlert({
        message: Array.isArray(e.data.message)
          ? e.data.message[0]
          : e.data.message,
        type: "error",
      });
    }
  };

  useEffect(() => {
    if (ref.current) {
      ref.current.focus();
    }
  }, []);

  useEffect(() => {
    if (tempUser) {
      dispatch(setUser(tempUser));
    }

    return () => {
      dispatch(setTempUser(null));
    };
  }, [dispatch, tempUser]);

  useEffect(() => {
    if (user && user.account.isTemporaryPassword) return;

    const newestChangedDate = dayjs
      .utc(user?.account.passwordChangePendedAt)
      .isAfter(user?.account.passwordChangedAt)
      ? user?.account.passwordChangePendedAt
      : user?.account.passwordChangedAt;

    const isSafePassword = dayjs
      .utc(newestChangedDate)
      .add(90, "day")
      .isAfter(utcNow());

    if (!isSafePassword) return;

    if (user && !user.account.isTemporaryPassword) {
      dispatch(setTempUser(null));

      return redirectOrNavigate(
        isBuyerType ? IMPORTER_PRIVATE_PATH.HOME : EXPORTER_PRIVATE_PATH.HOME
      );
    }
    if (!tempUser) {
      return goTo("LOGIN");
    }
  }, [dispatch, goTo, isBuyerType, redirectOrNavigate, tempUser, user]);

  return (
    <SignUpLayout>
      <FormSection>
        <HeaderSection>
          <PageTitle>
            <Typo typoType="d3" as="h1" color="gray1">
              {t("changePassword:pageTitle")}
            </Typo>
          </PageTitle>
          <SubTitle>
            <Typo typoType="b6r" color="gray4">
              {t("changePassword:cardDescription")}
            </Typo>
          </SubTitle>
        </HeaderSection>

        <StyledForm onSubmit={handleSubmit(handleChangePassword)}>
          <InputFieldsSection>
            <FieldDiv data-invalid={!!errors.currentPassword}>
              <Typo typoType="b7m" as="label">
                {t("changePassword:currentPassword")} <Required>*</Required>
              </Typo>
              <Controller
                name="currentPassword"
                rules={{
                  required: true,
                }}
                control={control}
                render={({ field }) => (
                  <StyledInput
                    {...field}
                    name="currentPassword"
                    type={passwordVisibleState.currentPassword}
                    icon={
                      <Icon
                        iconSrc={
                          passwordVisibleState.currentPassword === "password"
                            ? EyeOffSvg
                            : EyeSvg
                        }
                        iconSize={20}
                        onClick={() =>
                          passwordTypeDispatch({ type: "currentPassword" })
                        }
                      />
                    }
                    placeholder={t("changePassword:currentPasswordPlaceholder")}
                    onClear={() => setValue("currentPassword", "")}
                  />
                )}
              />

              {errors.currentPassword?.type === "required" && (
                <FormErrorMessage>
                  <StyledIcon iconSrc={ErrorCircleSvg} iconSize={16} />
                  {t("error:required")}
                </FormErrorMessage>
              )}
            </FieldDiv>

            <FieldDiv data-invalid={!!errors.newPassword}>
              <Typo typoType="b7m" as="label">
                {t("changePassword:newPassword")} <Required>*</Required>
              </Typo>
              <Controller
                name="newPassword"
                rules={{
                  required: true,
                  pattern: passwordRegex,
                }}
                control={control}
                render={({ field }) => (
                  <StyledInput
                    {...field}
                    name="newPassword"
                    type={passwordVisibleState.newPassword}
                    icon={
                      <StyledIcon
                        iconSrc={
                          passwordVisibleState.newPassword === "password"
                            ? EyeOffSvg
                            : EyeSvg
                        }
                        iconSize={20}
                        onClick={() =>
                          passwordTypeDispatch({ type: "newPassword" })
                        }
                      />
                    }
                    placeholder={t("changePassword:newPasswordPlaceholder")}
                    onClear={() => setValue("newPassword", "")}
                  />
                )}
              />

              {errors.newPassword?.type === "required" && (
                <FormErrorMessage>
                  <StyledIcon iconSrc={ErrorCircleSvg} iconSize={16} />
                  {t("error:required")}
                </FormErrorMessage>
              )}
              {errors.newPassword?.type === "pattern" && (
                <FormErrorMessage>
                  <StyledIcon iconSrc={ErrorCircleSvg} iconSize={16} />
                  {t("error:passwordPatterError")}
                </FormErrorMessage>
              )}
            </FieldDiv>

            <FieldDiv data-invalid={!!errors.rePassword}>
              <Typo typoType="b7m" as="label">
                {t("changePassword:rePassword")} <Required>*</Required>
              </Typo>
              <Controller
                name="rePassword"
                rules={{
                  required: true,
                  validate: (value, formValues) =>
                    value === formValues.newPassword,
                }}
                control={control}
                render={({ field }) => (
                  <StyledInput
                    {...field}
                    name="rePassword"
                    type={passwordVisibleState.reNewPassword}
                    icon={
                      <StyledIcon
                        iconSrc={
                          passwordVisibleState.reNewPassword === "password"
                            ? EyeOffSvg
                            : EyeSvg
                        }
                        iconSize={20}
                        onClick={() =>
                          passwordTypeDispatch({ type: "reNewPassword" })
                        }
                      />
                    }
                    placeholder={t("changePassword:rePasswordPlaceholder")}
                    onClear={() => setValue("rePassword", "")}
                  />
                )}
              />

              {errors.rePassword?.type === "required" && (
                <FormErrorMessage>
                  <StyledIcon iconSrc={ErrorCircleSvg} iconSize={16} />
                  {t("error:required")}
                </FormErrorMessage>
              )}
              {errors.rePassword?.type === "validate" && (
                <FormErrorMessage>
                  <StyledIcon iconSrc={ErrorCircleSvg} iconSize={16} />
                  {t("error:passwordRecheckFail")}
                </FormErrorMessage>
              )}
            </FieldDiv>
          </InputFieldsSection>

          <ButtonContainer>
            <LaterButton
              buttonColor="black"
              buttonGrade="tertiary"
              type="button"
              onClick={() => {
                dispatch(setTempUser(null));
                goTo("ROOT");
              }}
            >
              <Typo typoType="btn3m">{t("changePassword:later")}</Typo>
            </LaterButton>

            <SubmitButton type="submit">
              <Typo typoType="btn3m" color="white">
                {t("changePassword:resetButtonLabel")}
              </Typo>
            </SubmitButton>
          </ButtonContainer>
        </StyledForm>
      </FormSection>
    </SignUpLayout>
  );
};

export default ChangePasswordPage;

const FormSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  padding: 60px 40px;
  flex: 1;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const PageTitle = styled.div`
  padding: 24px 0;
  text-align: center;
  width: 100%;

  h1 {
    margin: 0;
  }
`;

const SubTitle = styled.div`
  text-align: center;
  padding: 8px 0;
`;

const InputFieldsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Required = styled.span`
  color: ${colorSet.red2};
`;

const FieldDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  &[data-invalid="true"] {
    .icon-input-container {
      border: 1px solid ${colorSet.red2};
    }
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
  max-width: 520px;
`;

const StyledIcon = styled(Icon)``;

const FormErrorMessage = styled.span`
  ${typo.b9r}
  color: ${colorSet.red2};
  padding-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;

  ${StyledIcon} {
    flex-shrink: 0;
  }
`;

const StyledInput = styled(IconInput)``;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const LaterButton = styled(Button)`
  width: 160px;
  text-align: center;
`;

const SubmitButton = styled(Button)`
  flex: 1;
  text-align: center;
`;
